/**
 * /l/:slug and /landing/:slug router — decides whether to mount the
 * production V1 experience (`LandingPage`) or the redesigned V2
 * experience (`LandingPageV2`) based on the landing page's stored
 * `flow_version` field.
 *
 * V1 is the default for every historical row (backend serialises
 * `flow_version="v1"` when absent) so this router is a pure
 * pass-through for existing pages — the byte-identical component,
 * fetch pattern and URL contract that shipped previously.
 *
 * V2 is opt-in per landing page (admin picks it in
 * System Settings → Landing Pages) and is only ever activated when
 * `starting_step === "guided_setup"`. Any other combination falls back
 * to V1 to guarantee the live flow keeps working.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { API_BASE } from '../lib/apiBase';
import { trackMarketingVisit } from '../lib/marketingVisit';
import LandingPage from './LandingPage';
import LandingPageV2 from './LandingPageV2';
import { useTranslation } from '../contexts/TranslationContext';

const API = `${API_BASE}/api`;

export default function LandingRouter() {
    const { slug } = useParams();
    const { applyEntryPointDefault } = useTranslation();
    const [flowVersion, setFlowVersion] = useState(null);
    const [startingStep, setStartingStep] = useState(null);
    const [resolved, setResolved] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (slug) trackMarketingVisit('landing_page', slug);
    }, [slug]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/public/landing-pages/${slug}`);
                if (cancelled) return;
                setFlowVersion(res.data?.flow_version || 'v1');
                setStartingStep(res.data?.starting_step || 'guided_setup');
                // Apply Landing Page default_language (priority 4). No-op
                // when priority 1/2/3 already resolved a language. Fired
                // here so both V1 and V2 pick up the choice.
                if (res.data?.default_language) {
                    applyEntryPointDefault(res.data.default_language);
                }
            } catch {
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setResolved(true);
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    if (!resolved) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    // On any resolution error, fall back to V1 so the historical page
    // renders its own error UI rather than showing a blank screen.
    if (error) return <LandingPage />;

    if (flowVersion === 'v2' && startingStep === 'guided_setup') {
        return <LandingPageV2 />;
    }
    return <LandingPage />;
}
